<template>
  <div>
    <button-toggle
      style="display: block; margin: 10px 0 20px; text-align: center;"
      :items="[
        { value: 'tossUp', text: 'Toss Up' },
        { value: 'gopProb', text: 'GOP %' },
        { value: 'demProb', text: 'Dem %' }
      ]"
      :selected="sortBy"
      @click="sortBy = $event">
    </button-toggle>
    <div id="scatter-table-title" style="height: 20px;"></div>
    <div id="scatter-table"></div>
    <loading class="loading-overlay" v-if="loading" :loading="loading" text="Loading Models"></loading>
    <div class="footnote" v-if="chamber === 'house'">* indicates an at-large district</div>
    <div class="flex-center">
      <v-btn
        v-if="chamber === 'house' && !showAllTossUp"
        style="margin-right: 10px;"
        depressed tile
        @click="showTossUps">
        Show Toss Ups
      </v-btn>
      <v-btn
        v-if="chamber === 'house' && !showAllCompetitive"
        style="margin-right: 10px;"
        depressed tile
        @click="showCompetitive">
        Show Competitive
      </v-btn>
      <v-btn
        v-if="showAll"
        depressed tile
        @click="showLess">
        Show Fewer
      </v-btn>
      <v-btn
        v-if="!showAll && chamber !== 'senateRunoff'"
        depressed tile
        @click="showMore">
        Show All
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as d3 from 'd3'
import cloneDeep from 'lodash.clonedeep'

import Utils from '@/utils'
import { ButtonToggle } from '@/components/common'
import Loading from '@/components/common/Loading'

const props = {
  data: {
    type: Array,
    required: true
  },
  chamber: {
    type: String,
    required: true
  },
  loading: {
    type: Boolean,
    required: false
  }
}

const components = {
  ButtonToggle,
  Loading
}

const computed = {
  ...mapGetters({
    screenWidth: 'ux/screenWidth',
    mobilePhone: 'ux/mobilePhone',
    mobile: 'ux/mobile',
  }),
  currentDate () {
    return this.$store.getters['aggregateModels/currentDate'](this.chamber)
  },
  sortedData () {
    const data = cloneDeep(this.data)
    if (this.sortBy === 'tossUp') {
      data.sort((a, b) => { return Math.abs(0.5 - a.gopWinProbability) - Math.abs(0.5 - b.gopWinProbability) })
    } else if (this.sortBy === 'gopProb') {
      data.sort((a, b) => { return b.gopWinProbability - a.gopWinProbability })
    } else if (this.sortBy === 'demProb') {
      data.sort((a, b) => { return a.gopWinProbability - b.gopWinProbability })
    }
    return data
  }
}

const methods = {
  showMore () {
    this.showAll = true
    this.showAllTossUp = false
    this.showAllCompetitive = false
    this.height = document.getElementById('g-table-wrapper').getBoundingClientRect().height + 10
    this.drawChart()
  },
  showTossUps () {
    this.showAllTossUp = true
    this.showAllCompetitive = false
    this.showAll = false
    const tossUps = this.data.filter(item => (item.gopWinProbability * 100) > 40 && (item.gopWinProbability * 100) <= 60).length
    this.height = (tossUps * 30) + 29
    this.drawChart()
  },
  showCompetitive () {
    this.showAllCompetitive = true
    this.showAllTossUp = false
    this.showAll = false
    const competitive = this.data.filter(item => (item.gopWinProbability * 100) >= 10 && (item.gopWinProbability * 100) <= 90).length
    this.height = (competitive * 30) + 29
    this.drawChart()
  },
  showLess () {
    this.showAll = false
    this.showAllTossUp = false
    this.showAllCompetitive = false
    this.height = 389
    this.drawChart()
  },
  getColor (v) {
    if (v < 35) {
      return 'var(--dem)'
    } else if (v > 65) {
      return 'var(--gop)'
    } else {
      return 'var(--purple)'
    }
  },
  drawChart () {
    const self = this

    const tooltip = d3.select('#app').append('div')
      .attr('class', 'scatter-table-tooltip')
      .style('opacity', 0)

    const chart = d3.select('#scatter-table')
    this.width = chart._groups[0][0].clientWidth

    chart.selectAll('*').remove()

    this.svg = chart.append('svg')
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('overflow', 'hidden')

    // make table layout - column/row lines and reference text
    this.table = this.svg.append('g').attr('id', 'g-table-wrapper')
    let table = this.table
    const data = this.sortedData

    let nameColWidthPerc = null
    let graphWidthPerc = null
    if (this.mobilePhone) {
      nameColWidthPerc = 20
      graphWidthPerc = 70
    } else if (this.mobile) {
      nameColWidthPerc = 10
      graphWidthPerc = 80
    } else {
      nameColWidthPerc = 20
      graphWidthPerc = 62
    }

    const columnsData = [
      { label: '0', labelText: '100', width: nameColWidthPerc },
      { label: '10', labelText: '90', width: graphWidthPerc * 0.1 },
      { label: '25', labelText: '75', width: graphWidthPerc * 0.15 },
      { label: '40', labelText: '60', width: graphWidthPerc * 0.15 },
      { label: '50', labelText: '50', width: graphWidthPerc * 0.1 },
      { label: '60', labelText: '60', width: graphWidthPerc * 0.1 },
      { label: '75', labelText: '75', width: graphWidthPerc * 0.15 },
      { label: '90', labelText: '90', width: graphWidthPerc * 0.15 },
      { label: '100', labelText: '100', width: graphWidthPerc * 0.1 }
    ]

    columnsData.forEach((col, index) => {
      const getWidth = (i) => {
        const arr = columnsData.filter((item, index) => index <= i)
        if (arr.length > 0) {
          const widths = arr.map(col => (col.width / 100) * this.width)
          return widths.reduce((a, b) => a + b)
        }
      }

      table.append('line')
        .attr('x1', getWidth(index))
        .attr('y1', () => {
          if (col.label === '50') {
            return 15
          } else {
            return 28
          }
        })
        .attr('x2', getWidth(index))
        .attr('y2', this.height)
        .style('stroke-width', () => {
          if (col.label === '50') {
            return 1
          } else {
            return 0.25
          }
        })
        .style('stroke', this.$vuetify.theme.currentTheme.text)
        .style('fill', 'none')
        .style('display', () => {
          if (this.mobilePhone && !['0', '25', '50', '75', '100'].includes(col.label)) {
            return 'none'
          }
        })

      table.append('text')
        .attr('x', getWidth(index) - (col.label.length * 4))
        .attr('y', 22)
        .style('font-size', () => {
          if (this.mobilePhone) {
            return '0.8em'
          } else {
            return '0.9em'
          }
        })
        .style('font-weight', 500)
        .style('fill', () => {
          return this.getColor(col.label)
        })
        .style('display', () => {
          if (this.mobilePhone && !['0', '25', '75', '100'].includes(col.label)) {
            return 'none'
          }
          if (!this.mobile && ['0', '100'].includes(col.label)) {
            return 'none'
          }
        })
        .text(() => {
          if (col.label !== '50') {
            return col.labelText + '%'
          }
        })
    })

    const rowHeight = 30
    const getRowHeight = (index) => {
      return (index * rowHeight) + 28
    }

    data.forEach((item, index) => {
      table.append('line')
        .attr('x1', 0)
        .attr('y1', getRowHeight(index))
        .attr('x2', this.width)
        .attr('y2', getRowHeight(index))
        .style('stroke-width', 0.25)
        .style('stroke', this.$vuetify.theme.currentTheme.text)
        .style('fill', 'none')
    })

    // calc width of name column
    const nameColWidthPixels = (nameColWidthPerc / 100) * this.width
    // calc width of graph columns
    let graphColWidths = columnsData.map((col) => col.width)
    graphColWidths.shift()
    const graphWidthPixels = (graphColWidths.reduce((a, b) => a + b)) / 100

    const title = d3.select('#scatter-table-title')
    const titleWidth = title._groups[0][0].clientWidth

    title.selectAll('*').remove()

    const titleSvg = title.append('svg')
      .attr('width', titleWidth)
      .attr('height', 20)

    const demTitle = titleSvg.append('g')
      .attr('class', 'dem-title')
    demTitle.append('text')
      .attr('x', ((this.width * graphWidthPixels) * 0.25) + nameColWidthPixels)
      .attr('y', 12)
      .style('font-size', '0.9em')
      .style('font-weight', 500)
      .style('letter-spacing', '0.05em')
      .style('text-anchor', 'middle')
      .style('fill', 'var(--text-color)')
      .text('Dem Majority')

    const gopTitle = titleSvg.append('g')
      .attr('class', 'gop-title')
    gopTitle.append('text')
      .attr('x', ((this.width * graphWidthPixels) * 0.75) + nameColWidthPixels)
      .attr('y', 12)
      .style('font-size', '0.9em')
      .style('font-weight', 500)
      .style('letter-spacing', '0.05em')
      .style('text-anchor', 'middle')
      .style('fill', 'var(--text-color)')
      .text('GOP Majority')

    data.forEach((item, index) => {
      let district = table.append('g')
        .attr('class', 'district')

      const currentRowHeight = getRowHeight(index + 1)

      district.append('text')
        .attr('class', 'table-row-name')
        .attr('x', 5)
        .attr('y', () => {
          const height = getRowHeight(index + 1)
          return height - (rowHeight * 0.35)
        })
        .style('font-size', '0.9em')
        .attr('fill', this.$vuetify.theme.currentTheme.text)
        .style('font-weight', 500)
        .text(() => {
          let election = null
          if (this.chamber === 'house') {
            let cd = null
            if (item.election.cd.includes('At-Large')) {
              cd = '*'
            } else {
              cd = ' ' + item.election.cd
            }
            if (this.mobile) {
              election = item.election.state + cd
            } else {
              election = item.election.raceName
            }
          } else {
            item.election.specialElection ? election = item.election.raceName + '*' : election = item.election.raceName
            if (this.mobile || item.election.state === 'DC') {
              const name = item.election.cd ? `${item.election.state} ${item.election.cd}` : item.election.state
              item.election.specialElection ? election = name + '*' : election = name
            }
          }
          return election.replace(" Senate", "").replace(" House", "");
        })

      if (!this.mobile) {
        const obj = Utils.getColorScale(item.rating)
        const tags = district.append('g')

        if (obj) {
          tags.append('rect')
            .attr('class', 'tag-rect')
            .attr('x', (this.width * graphWidthPixels) + nameColWidthPixels + 16)
            .attr('y', currentRowHeight - 25)
            .attr('width', 68)
            .attr('height', 20)
            .attr('fill', obj.color)

          tags.append('text')
            .attr('class', 'race-state-text')
            .attr('x', (this.width * graphWidthPixels) + nameColWidthPixels + 50)
            .attr('y', currentRowHeight - (rowHeight * 0.35))
            .style('font-size', '0.8em')
            .style('fill', obj.textColor)
            .style('font-weight', 500)
            .style('text-anchor', 'middle')
            .style('text-transform', 'uppercase')
            .text(obj.state)
        }
      }

      // create chevron arrows
      const arrowFunc = d3.line()
        .x((d) => { return d.x })
        .y((d) => { return d.y })
        .curve(d3.curveLinear)

      const arrowData = [
        {
          'x': this.width - 18,
          'y': currentRowHeight - (rowHeight * 0.7)
        },
        {
          'x': this.width - 10,
          'y': currentRowHeight - (rowHeight * 0.7) + 7
        },
        {
          'x': this.width - 18,
          'y': currentRowHeight - (rowHeight * 0.7) + 14
        }
      ]

      district.append('path')
        .attr('d', arrowFunc(arrowData))
        .attr('stroke', '#BDBDBD')
        .attr('stroke-width', 2)
        .attr('fill', 'rgba(0,0,0,0)')

      district.append('rect')
        .attr('class', 'table-row')
        .attr('x', 0)
        .attr('y', getRowHeight(index))
        .attr('width', this.width)
        .attr('height', rowHeight)
        .style('pointer-events', 'visible')
        .on('click', () => {
          this.$router.push(`/${this.chamber}/${item.election.raceId}`)
        })

      const color = this.getColor(item.gopWinProbability * 100)

      const outerCircle = district.append('circle')
        .attr('class', 'outer-circle')
        .attr('cy', currentRowHeight - (rowHeight * 0.5))
        .attr('r', 10)
        .style('stroke', () => {
          return color
        })
        .style('fill', this.$vuetify.theme.currentTheme.background)
        .style('stroke-width', 4)
        .style('cursor', 'pointer')
        .on('click', () => {
          tooltip.transition()
            .duration(100)
            .style('opacity', 0)
          this.$router.push(`/${this.chamber}/${item.election.raceId}`)
        })

      const innerCircle = district.append('circle')
        .attr('class', 'inner-circle')
        .attr('cy', currentRowHeight - (rowHeight * 0.5))
        .attr('r', 5)
        .style('fill', () => {
          return this.getColor(item.gopWinProbability * 100)
        })
        .style('stroke-width', 0)
        .attr('pointer-events', 'none')

      if (!this.mobile) {
        const perc = item.gopWinProbability >= 0.5 ? (item.gopWinProbability * 100).toFixed(2) : ((1 - item.gopWinProbability) * 100).toFixed(2)
        outerCircle
          .attr('cx', 0.5 * (this.width * graphWidthPixels) + nameColWidthPixels)
          .on('mousemove', function () {
            tooltip.transition()
              .duration(100)
              .style('opacity', 1)
            tooltip.html(perc + '%')
              .style('left', (d3.event.pageX - 34) + 'px')
              .style('top', (d3.event.pageY - 42) + 'px')
              .append('div').attr('class', 'scatter-table-tooltip-arrow')
            tooltip.append('div').attr('class', 'scatter-table-tooltip-top')
            d3.select(this).style('fill', '#bdbdbd')
          })
          .on('mouseout', function () {
            tooltip.transition()
              .duration(100)
              .style('opacity', 0)
            d3.select(this).style('fill', self.$vuetify.theme.currentTheme.background)
          })

        innerCircle
          .attr('cx', 0.5 * (this.width * graphWidthPixels) + nameColWidthPixels)
      }
    })

    this.svg.selectAll('.outer-circle')
      .data(this.sortedData)
      .attr('cx', (d) => {
        return d.gopWinProbability * (this.width * graphWidthPixels) + nameColWidthPixels
      })

    this.svg.selectAll('.inner-circle')
      .data(this.sortedData)
      .attr('cx', (d) => {
        return d.gopWinProbability * (this.width * graphWidthPixels) + nameColWidthPixels
      })
  }
}

export default {
  props,
  components,
  computed,
  methods,
  data () {
    return {
      width: null,
      height: null,
      svg: null,
      table: null,
      showAll: false,
      showAllTossUp: false,
      showAllCompetitive: false,
      sortBy: 'tossUp',
      tags: []
    }
  },
  mounted () {
    this.height = 389
    if (this.chamber === 'senateRunoff') {
      this.height = 88
    }
    if (this.chamber === 'house') {
      this.showAllTossUp = true
    }
    if (this.chamber === 'house' && this.showAllTossUp) {
      this.showTossUps()
    } else {
      this.drawChart()
    }
  },
  watch: {
    screenWidth () {
      this.drawChart()
    },
    data () {
      this.drawChart()
    },
    sortBy () {
      this.drawChart()
    },
    '$vuetify.theme.isDark'() {
      this.drawChart()
    }
  }
}
</script>

<style>
#scatter-table {
  position: relative;
}

rect.table-row {
  fill: #BDBDBD;
  fill-opacity: 0;
}

.table-row:hover {
  cursor: pointer;
  fill-opacity: 0.2;
}

.scatter-table-tooltip-top {
  width: 50px;
  height: 8px;
  bottom: 0;
  position: absolute;
  background: #fff;
}

.scatter-table-tooltip {
  font-family: 'Roboto';
  position: absolute;
  text-align: center;
  width: 66px;
  height: 32px;
  padding: 5px;
  font-size: 15px;
  font-weight: 500;
  background: #fff;
  color: black;
  border: 1px solid black;
  pointer-events: none;
}

.scatter-table-tooltip-arrow {
  position: absolute;
  background: #fff;
  border: 1px solid #222;
  bottom: -6px;
  left: 26px;
  transform: rotate(45deg);
  width: 12px;
  height: 12px;
}

.loading-overlay {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translateX(-50%);
}

.loading-overlay p {
  font-weight: 600;
  color: var(--gop);
  letter-spacing: 0.05em;
  padding: 0 5px;
  background: linear-gradient(rgba(245, 245, 245, 0.1) 0%, rgba(245, 245, 245, 0.7) 50%, rgba(245, 245, 245, 0.1) 100%);
}
</style>
